'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatAttachment, { ChatMediaPreview, type ChatMedia } from '@/components/ChatAttachment';
import { createClient } from '@/lib/local-api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import {
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  CheckIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

interface UserProfile {
  id: string;
  full_name: string;
  username?: string;
  avatar_url?: string;
}

interface Conversation {
  partner: UserProfile;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  lastSenderId: string;
}

interface Message extends ChatMedia {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function Avatar({ profile, size = 'md' }: { profile: UserProfile; size?: 'sm' | 'md' | 'lg' }) {
  const initials = profile.full_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';
  const sizeClass = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm';
  return (
    <div className={`${sizeClass} rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center font-bold text-white`}
      style={{ background: 'linear-gradient(135deg, #3b6ef5 0%, #6c3bf5 100%)' }}
    >
      {profile.avatar_url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={profile.avatar_url} alt={profile.full_name} className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );
}

export default function MessagesContent() {
  const { user } = useAuth();
  return <AccountMessages key={user?.id ?? 'signed-out'} />;
}

function AccountMessages() {
  const { user } = useAuth();
  const supabase = createClient();
  const { markDmsRead } = useRealtime();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState<UserProfile | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const messageListRef = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageRequest = useRef(0);

  useEffect(() => {
    if (!user) return;
    const partnerId = new URLSearchParams(window.location.search).get('with');
    if (!partnerId || partnerId === user.id) return;
    let cancelled = false;
    createClient().from('user_profiles').select('*').eq('id', partnerId).maybeSingle()
      .then(({ data }: { data: UserProfile | null }) => {
        if (data && !cancelled) setSelectedPartner(data);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const scrollToBottom = useCallback(() => {
    const list = messageListRef.current;
    if (list && followLatest.current) list.scrollTop = list.scrollHeight;
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!user) return;
    setLoadingConvs(true);
    try {
      // Get all messages involving the current user
      const { data: msgs } = await supabase
        .from('direct_messages')
        .select('id, sender_id, receiver_id, content, read_at, created_at')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (!msgs) { setLoadingConvs(false); return; }

      // Group by conversation partner
      const convMap = new Map<string, { msgs: Message[]; partnerId: string }>();
      msgs.forEach((m) => {
        const partnerId = m.sender_id === user.id ? m.receiver_id : m.sender_id;
        if (!convMap.has(partnerId)) {
          convMap.set(partnerId, { msgs: [], partnerId });
        }
        convMap.get(partnerId)!.msgs.push(m as Message);
      });

      // Fetch partner profiles
      const partnerIds = Array.from(convMap.keys());
      if (partnerIds.length === 0) { setConversations([]); setLoadingConvs(false); return; }

      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, avatar_url')
        .in('id', partnerIds);

      const profileMap = new Map<string, UserProfile>();
      profiles?.forEach((p) => profileMap.set(p.id, p as UserProfile));

      const convList: Conversation[] = [];
      convMap.forEach(({ msgs: convMsgs, partnerId }) => {
        const partner = profileMap.get(partnerId);
        if (!partner) return;
        const sorted = [...convMsgs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const latest = sorted[0];
        const unread = convMsgs.filter((m) => m.receiver_id === user.id && !m.read_at).length;
        convList.push({
          partner,
          lastMessage: latest.content,
          lastMessageAt: latest.created_at,
          unreadCount: unread,
          lastSenderId: latest.sender_id,
        });
      });

      convList.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
      setConversations(convList);
    } finally {
      setLoadingConvs(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (partnerId: string) => {
    if (!user) return;
    const requestId = ++messageRequest.current;
    followLatest.current = true;
    setMessages([]);
    setLoadingMsgs(true);
    try {
      const { data } = await supabase
        .from('direct_messages')
        .select('*')
        .or(
          `and(sender_id.eq.${user.id},receiver_id.eq.${partnerId}),and(sender_id.eq.${partnerId},receiver_id.eq.${user.id})`
        )
        .order('created_at', { ascending: true });
      if (requestId !== messageRequest.current) return;
      setMessages((data as Message[]) || []);

      // Mark unread messages as read
      const unreadIds = (data as Message[])
        ?.filter((m) => m.receiver_id === user.id && !m.read_at)
        .map((m) => m.id);
      if (unreadIds && unreadIds.length > 0) {
        await supabase
          .from('direct_messages')
          .update({ read_at: new Date().toISOString() })
          .in('id', unreadIds);
        // Refresh conversations to update unread counts
        loadConversations();
      }
    } finally {
      if (requestId === messageRequest.current) setLoadingMsgs(false);
    }
  }, [user, supabase, loadConversations]);

  useEffect(() => {
    if (selectedPartner) {
      loadMessages(selectedPartner.id);
    }
  }, [selectedPartner, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loadingMsgs, scrollToBottom]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!user || !selectedPartner) return;
    const channel = supabase
      .channel(`dm_${user.id}_${selectedPartner.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
        },
        (payload) => {
          const msg = payload.new as Message;
          const isRelevant =
            (msg.sender_id === user.id && msg.receiver_id === selectedPartner.id) ||
            (msg.sender_id === selectedPartner.id && msg.receiver_id === user.id);
          if (isRelevant) {
            setMessages((prev) => {
              if (prev.find((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
            // Mark as read if received
            if (msg.receiver_id === user.id) {
              void supabase
                .from('direct_messages')
                .update({ read_at: new Date().toISOString() })
                .eq('id', msg.id).then(() => markDmsRead());
            }
            loadConversations();
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, selectedPartner, supabase, loadConversations]);

  // Real-time for inbox updates (when no conversation open)
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`dm_inbox_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${user.id}`,
        },
        () => loadConversations()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, supabase, loadConversations]);

  // Search users
  useEffect(() => {
    if (!searchQuery.trim() || !user) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, avatar_url')
        .neq('id', user.id)
        .or(`full_name.ilike.%${searchQuery}%,username.ilike.%${searchQuery}%`)
        .limit(8);
      setSearchResults((data as UserProfile[]) || []);
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, user, supabase]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedPartner || !user || sending) return;
    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);
    try {
      const { data, error } = await supabase
        .from('direct_messages')
        .insert({
          sender_id: user.id,
          receiver_id: selectedPartner.id,
          content,
        })
        .select()
        .single();
      if (!error && data) {
        followLatest.current = true;
        setMessages((prev) => {
          if (prev.find((m) => m.id === data.id)) return prev;
          return [...prev, data as Message];
        });
        loadConversations();
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openConversation = (partner: UserProfile) => {
    setSelectedPartner(partner);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    // Reset global DM badge when user opens a conversation
    markDmsRead();
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <ChatBubbleLeftRightIcon className="w-16 h-16 text-muted-foreground/30" />
        <p className="text-foreground font-semibold text-lg">Sign in to view messages</p>
        <p className="text-muted-foreground text-sm">You need to be logged in to send and receive messages.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-0 min-h-0 h-[calc(100dvh-11.5rem)] lg:h-[calc(100dvh-3rem)] rounded-2xl overflow-hidden border border-border/60"
      style={{ boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}
    >
      {/* ── Inbox / Conversation List ── */}
      <div className={`${selectedPartner ? 'hidden lg:flex' : 'flex'} min-h-0 flex-col w-full lg:w-80 xl:w-96 flex-shrink-0 bg-card border-r border-border/60`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border/50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.history.back()}
              className="lg:hidden p-1.5 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-base font-bold text-foreground">Messages</h1>
          </div>
          <button
            onClick={() => setShowSearch((s) => !s)}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
            title="New message"
          >
            {showSearch ? <XMarkIcon className="w-5 h-5" /> : <MagnifyingGlassIcon className="w-5 h-5" />}
          </button>
        </div>

        {/* Search / New conversation */}
        {showSearch && (
          <div className="px-3 py-2 border-b border-border/50">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                placeholder="Search people..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-muted text-sm text-foreground placeholder:text-muted-foreground border border-border/50 focus:outline-none focus:border-primary/50 transition-colors"
              />
            </div>
            {searching && (
              <p className="text-xs text-muted-foreground px-1 py-2">Searching...</p>
            )}
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-0.5 max-h-60 overflow-y-auto">
                {searchResults.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => openConversation(p)}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-muted transition-all text-left"
                  >
                    <Avatar profile={p} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{p.full_name}</p>
                      {p.username && <p className="text-xs text-muted-foreground truncate">@{p.username}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchQuery && !searching && searchResults.length === 0 && (
              <p className="text-xs text-muted-foreground px-1 py-2">No users found</p>
            )}
          </div>
        )}

        {/* Conversation list */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-thin">
          {loadingConvs ? (
            <div className="flex flex-col gap-3 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-2.5 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-6 py-12">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-muted-foreground/30" />
              <p className="text-sm font-semibold text-foreground text-center">No conversations yet</p>
              <p className="text-xs text-muted-foreground text-center">
                Click the search icon above to find someone and start a conversation.
              </p>
            </div>
          ) : (
            <div className="py-2">
              {conversations.map((conv) => {
                const isActive = selectedPartner?.id === conv.partner.id;
                return (
                  <button
                    key={conv.partner.id}
                    onClick={() => openConversation(conv.partner)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-all text-left ${
                      isActive ? 'bg-primary/10 border-r-2 border-primary' : 'hover:bg-muted/60'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <Avatar profile={conv.partner} />
                      {conv.unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center"
                          style={{ boxShadow: '0 1px 6px rgba(59,110,245,0.5)' }}
                        >
                          {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className={`text-sm truncate ${conv.unreadCount > 0 ? 'font-bold text-foreground' : 'font-semibold text-foreground'}`}>
                          {conv.partner.full_name}
                        </p>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatTime(conv.lastMessageAt)}</span>
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {conv.lastSenderId === user?.id ? 'You: ' : ''}{conv.lastMessage}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Conversation Thread ── */}
      <div className={`${selectedPartner ? 'flex' : 'hidden lg:flex'} min-h-0 min-w-0 flex-col flex-1 bg-background`}>
        {selectedPartner ? (
          <>
            {/* Thread header */}
            <div className="shrink-0 flex items-center gap-3 px-4 py-3.5 border-b border-border/60 bg-card">
              <button
                onClick={() => setSelectedPartner(null)}
                className="lg:hidden p-1.5 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
              <Avatar profile={selectedPartner} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{selectedPartner.full_name}</p>
                {selectedPartner.username && (
                  <p className="text-xs text-muted-foreground truncate">@{selectedPartner.username}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div ref={messageListRef}
              onScroll={(event) => {
                const list = event.currentTarget;
                followLatest.current = list.scrollHeight - list.scrollTop - list.clientHeight < 64;
              }}
              className="min-h-0 overscroll-contain flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-3">
              {loadingMsgs ? (
                <div className="flex flex-col gap-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                      <div className={`h-9 rounded-2xl bg-muted animate-pulse ${i % 2 === 0 ? 'w-48' : 'w-40'}`} />
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="w-7 h-7 text-primary" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Start a conversation</p>
                  <p className="text-xs text-muted-foreground">Send your first message to {selectedPartner.full_name}</p>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => {
                    const isMine = msg.sender_id === user?.id;
                    const prevMsg = messages[idx - 1];
                    const showDate =
                      !prevMsg ||
                      new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="flex items-center justify-center my-2">
                            <span className="text-[10px] text-muted-foreground bg-muted px-3 py-1 rounded-full">
                              {new Date(msg.created_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        )}
                        <div className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
                          {!isMine && (
                            <Avatar profile={selectedPartner} size="sm" />
                          )}
                          <div className={`max-w-[72%] lg:max-w-[60%]`}>
                            <div
                              className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                isMine
                                  ? 'text-white rounded-br-sm' :'bg-muted text-foreground rounded-bl-sm'
                              }`}
                              style={isMine ? {
                                background: 'linear-gradient(135deg, #3b6ef5 0%, #5b4bf5 100%)',
                                boxShadow: '0 2px 12px rgba(59,110,245,0.3)',
                              } : {}}
                            >
                              <ChatMediaPreview message={msg} />{msg.content}
                            </div>
                            <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                              <span className="text-[10px] text-muted-foreground">{formatTime(msg.created_at)}</span>
                              {isMine && (
                                msg.read_at
                                  ? <CheckCircleIcon className="w-3 h-3 text-primary" />
                                  : <CheckIcon className="w-3 h-3 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>

            {/* Message input */}
            <div className="shrink-0 px-4 py-3 border-t border-border/60 bg-card">
              <div className="flex items-center gap-2">
                <ChatAttachment key={selectedPartner.id} senderId={user.id} receiverId={selectedPartner.id} onSent={(message) => {
                  followLatest.current = true;
                  setMessages((previous) => previous.some((item) => item.id === message.id) ? previous : [...previous, message]);
                  loadConversations();
                }} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={`Message ${selectedPartner.full_name}...`}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="min-w-0 flex-1 px-4 py-2.5 rounded-xl bg-muted text-sm text-foreground placeholder:text-muted-foreground border border-border/50 focus:outline-none focus:border-primary/50 transition-colors"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="p-2.5 rounded-xl text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #3b6ef5 0%, #5b4bf5 100%)',
                    boxShadow: newMessage.trim() ? '0 2px 12px rgba(59,110,245,0.4)' : 'none',
                  }}
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-4 px-8">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center"
              style={{ boxShadow: '0 4px 24px rgba(59,110,245,0.15)' }}
            >
              <ChatBubbleLeftRightIcon className="w-10 h-10 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-foreground mb-1">Your Messages</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Select a conversation from the left or search for someone to start messaging.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
